import styled from "styled-components";
import { useEffect, useState } from "react";
import CaretDown from "../assets/Caret Down.svg";

const StaffFilterContainer = styled.div`
  position: relative;
  margin-right: 1rem;

  @media (max-width: 1200px) {
    margin-right: 0.5rem;
  }
`;

const StaffFilterButton = styled.button<{
  $isOpen: boolean;
  $compact?: boolean;
}>`
  padding: ${(props) => (props.$compact ? "0" : "0.5rem 1rem")};
  border-radius: ${(props) => (props.$compact ? "1.5rem" : "0.5rem")};
  border: 1px solid ${({ theme }) => theme.secondaryGrey};
  background-color: ${({ theme }) => theme.white};
  color: ${({ theme }) => theme.secondary};
  font-size: 0.875rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  height: 3rem;
  min-width: ${(props) => (props.$compact ? "3rem" : "120px")};
  width: ${(props) => (props.$compact ? "3rem" : "auto")};
  white-space: nowrap;
  transition: all 0.2s ease;

  @media (max-width: 900px) {
    padding: 0;
    min-width: 3rem;
    width: 3rem;
    border-radius: 1.5rem;

    span {
      display: none;
    }

    img {
      margin: 0;
    }
  }

  &:hover {
    border-color: ${({ theme }) => theme.primary};
    background-color: ${({ theme }) => theme.primaryLight};
  }

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.primary};
  }

  img {
    transition: transform 0.2s ease;
    transform: ${({ $isOpen }) => ($isOpen ? "rotate(180deg)" : "rotate(0)")};
  }
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: calc(100% + 0.5rem);
  right: 0;
  background-color: white;
  border-radius: 0.75rem;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  min-width: 200px;
  z-index: 1000;
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid ${({ theme }) => theme.secondaryGrey};

  @media (max-width: 900px) {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 90%;
    max-width: 300px;
  }

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.secondaryGrey};
    border-radius: 3px;
  }
`;

const DropdownItem = styled.div<{ $isSelected: boolean }>`
  padding: 0.75rem 1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background-color: ${({ $isSelected, theme }) =>
    $isSelected ? theme.primaryLight : "white"};
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${({ theme, $isSelected }) =>
      $isSelected ? theme.primaryLight : "#f5f5f5"};
  }

  &:first-child {
    border-radius: 0.75rem 0.75rem 0 0;
  }

  &:last-child {
    border-radius: 0 0 0.75rem 0.75rem;
  }
`;

const ColorDot = styled.span<{ $color: string }>`
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: ${(props) => props.$color};
  flex-shrink: 0;
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.3);
  z-index: 999;
  animation: fadeIn 0.2s ease;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

interface StaffOption {
  name: string;
  color: string;
}

interface Props {
  options: StaffOption[];
  value: string[];
  onChange: (selected: string[]) => void;
  compact?: boolean;
}

export const StaffFilter = ({
  options,
  value,
  onChange,
  compact = false,
}: Props) => {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (isOpen && !target.closest("[role='stafffilter']")) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const toggleOption = (optionName: string) => {
    const newValue = value.includes(optionName)
      ? value.filter((v) => v !== optionName)
      : [...value, optionName];
    onChange(newValue);
  };

  const selectedCount = value.length;
  return (
    <StaffFilterContainer role="stafffilter">
      <StaffFilterButton
        $isOpen={isOpen}
        $compact={compact}
        onClick={() => setIsOpen(!isOpen)}
        title={
          selectedCount ? `${selectedCount} staff selected` : "Filter by staff"
        }
      >
        {!compact && (
          <span>
            {selectedCount ? `${selectedCount} Selected` : "Filter by Staff"}
          </span>
        )}
        <img src={CaretDown} alt="Toggle staff filter" />
      </StaffFilterButton>

      {isOpen && (
        <>
          <DropdownMenu>
            {options.map((option, index) => (
              <DropdownItem
                key={index}
                $isSelected={value.includes(option.name)}
                onClick={() => toggleOption(option.name)}
                title={option.name}
              >
                <ColorDot $color={option.color} />
                {option.name}
              </DropdownItem>
            ))}
          </DropdownMenu>
          <Overlay onClick={() => setIsOpen(false)} />
        </>
      )}
    </StaffFilterContainer>
  );
};
